/**
 * PaymentHistoryTimeline Component
 *
 * Displays a chronological list of payments and credit notes for an invoice.
 * Shows date, type, amount, and reference/reason for each entry.
 * Includes running balance calculation and Xero sync status for credit notes.
 *
 * User Story: US-REC-001 - Invoice Reconciliation
 */

import { useMemo, useState, useCallback } from 'react'
import { parseDateString } from '@/utils/dateUtils'
import { CreditNoteTypeBadge } from '@/components/credit-notes/CreditNoteTypeBadge'
import { XeroSyncStatusIndicator } from '@/components/credit-notes/XeroSyncStatusIndicator'
import { GoCardlessStatusBadge } from '@/components/GoCardlessStatusBadge'
import { CreditCardIcon, ReceiptIcon, CalendarIcon } from 'lucide-react'
import { cn } from '@/lib/utils'

/**
 * Format currency value
 * @param {number} amount - Amount to format
 * @returns {string} Formatted currency string
 */
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP'
  }).format(amount)
}

/**
 * Format date for display
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date
 */
const formatDate = (dateString) => {
  if (!dateString) return '-'
  return parseDateString(dateString).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  })
}

/**
 * TimelineItem component for individual entries
 * @param {Object} props - Component props
 * @param {Object} props.entry - Timeline entry data
 * @param {boolean} props.isLast - Whether this is the last item
 * @param {number} props.runningBalance - Running balance after this entry
 * @param {function} props.onRetrySync - Callback for retrying failed syncs
 * @param {string} props.retryingId - ID of credit note currently retrying sync
 */
function TimelineItem({ entry, isLast, runningBalance, onRetrySync, retryingId }) {
  const isPayment = entry.type === 'payment'
  const isCreditNote = entry.type === 'credit_note'
  const isGoCardless = isPayment && entry.source === 'gocardless'
  const isGcFailed = isGoCardless && ['failed', 'charged_back', 'late_failure_settled'].includes(entry.gcStatus)
  const hasSyncFailed = isCreditNote && entry.syncStatus === 'failed'

  return (
    <div className={cn(
      'relative pb-6',
      hasSyncFailed && 'bg-amber-50/50 -mx-2 px-2 rounded-md'
    )}>
      {/* Timeline connector line */}
      {!isLast && (
        <span
          className="absolute left-4 top-8 -ml-px h-full w-0.5 bg-gray-200"
          aria-hidden="true"
        />
      )}

      <div className="relative flex items-start gap-4">
        {/* Icon */}
        <div
          className={cn(
            'flex h-8 w-8 items-center justify-center rounded-full',
            isPayment
              ? isGcFailed
                ? 'bg-red-100 text-red-600'
                : 'bg-green-100 text-green-600'
              : hasSyncFailed
                ? 'bg-amber-100 text-amber-600'
                : 'bg-blue-100 text-blue-600'
          )}
        >
          {isPayment ? (
            <CreditCardIcon className="h-4 w-4" aria-hidden="true" />
          ) : (
            <ReceiptIcon className="h-4 w-4" aria-hidden="true" />
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium text-gray-900">
                {isPayment ? 'Payment' : 'Credit Note'}
              </span>
              {isGoCardless && entry.gcStatus && (
                <GoCardlessStatusBadge type="payment" status={entry.gcStatus} />
              )}
              {isCreditNote && entry.creditNoteType && (
                <CreditNoteTypeBadge
                  type={entry.creditNoteType}
                  syncStatus={entry.syncStatus}
                  syncError={entry.syncError}
                  xeroNumber={entry.xeroNumber}
                  onRetry={entry.syncStatus === 'failed' && onRetrySync
                    ? () => onRetrySync(entry.id)
                    : undefined}
                  retrying={retryingId === entry.id}
                />
              )}
            </div>
            <span className="text-sm font-semibold text-gray-900">
              {formatCurrency(entry.amount)}
            </span>
          </div>

          <div className="mt-1 flex items-center gap-2 text-xs text-gray-500">
            <CalendarIcon className="h-3 w-3" aria-hidden="true" />
            <time dateTime={entry.date}>{formatDate(entry.date)}</time>
            {entry.reference && (
              <>
                <span className="text-gray-300">|</span>
                <span className="truncate" title={entry.reference}>
                  {entry.reference}
                </span>
              </>
            )}
          </div>

          {entry.reason && (
            <p className="mt-1 text-xs text-gray-600 line-clamp-2">
              {entry.reason}
            </p>
          )}

          {/* GoCardless failure reason */}
          {isGcFailed && entry.gcFailureReason && (
            <p className="mt-1 text-xs text-red-600">
              {entry.gcFailureReason}
            </p>
          )}

          {/* Sync error message for failed credit notes */}
          {hasSyncFailed && entry.syncError && (
            <p className="mt-1 text-xs text-amber-600">
              Sync error: {entry.syncError}
            </p>
          )}

          {/* Running balance */}
          <div className="mt-2 text-xs text-gray-500">
            Balance after: {formatCurrency(runningBalance)}
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * PaymentHistoryTimeline displays chronological payment and credit history
 * @param {Object} props - Component props
 * @param {Array} props.payments - Array of payment records
 * @param {Array} props.creditNotes - Array of credit note records
 * @param {number} props.invoiceTotal - Total invoice amount (for balance calculation)
 * @param {function} props.onRetryCreditNoteSync - Callback for retrying credit note sync
 * @param {string} props.className - Additional CSS classes
 */
export function PaymentHistoryTimeline({
  payments = [],
  creditNotes = [],
  invoiceTotal = 0,
  onRetryCreditNoteSync,
  className
}) {
  // Track which credit note is currently retrying
  const [retryingId, setRetryingId] = useState(null)

  // Handle retry with loading state
  const handleRetrySync = useCallback(async (creditNoteId) => {
    if (!onRetryCreditNoteSync) return

    setRetryingId(creditNoteId)
    try {
      await onRetryCreditNoteSync(creditNoteId)
    } catch (err) {
      console.error('[PaymentHistoryTimeline] Retry failed:', err)
    } finally {
      setRetryingId(null)
    }
  }, [onRetryCreditNoteSync])

  // Combine and sort entries chronologically
  const timelineEntries = useMemo(() => {
    const paymentEntries = payments.map(p => ({
      id: p.id,
      type: 'payment',
      date: p.date || p.payment_date,
      amount: parseFloat(p.amount) || 0,
      reference: p.reference || p.payment_method || p.method,
      reason: p.notes,
      source: p.source,
      gcStatus: p.gocardless_payment_status,
      gcChargeDate: p.gocardless_charge_date,
      gcFailureReason: p.gocardless_failure_reason
    }))

    const creditEntries = creditNotes.map(cn => ({
      id: cn.id,
      type: 'credit_note',
      creditNoteType: cn.type,
      date: cn.date,
      amount: parseFloat(cn.amount) || 0,
      reference: cn.credit_note_no,
      reason: cn.reason,
      // Xero sync status fields
      syncStatus: cn.xero_sync_status,
      syncError: cn.xero_sync_error,
      xeroNumber: cn.xero_credit_note_number
    }))

    // Combine and sort by date (oldest first for running balance calculation)
    return [...paymentEntries, ...creditEntries].sort((a, b) => {
      const dateA = new Date(a.date || 0)
      const dateB = new Date(b.date || 0)
      return dateA - dateB
    })
  }, [payments, creditNotes])

  // Calculate running balances
  const entriesWithBalance = useMemo(() => {
    let balance = invoiceTotal
    return timelineEntries.map(entry => {
      balance -= entry.amount
      return { ...entry, runningBalance: balance }
    })
  }, [timelineEntries, invoiceTotal])

  // Reverse for display (most recent first)
  const displayEntries = useMemo(() => {
    return [...entriesWithBalance].reverse()
  }, [entriesWithBalance])

  // Count failed syncs for attention indicator (must be before early return)
  const failedSyncCount = useMemo(() => {
    return displayEntries.filter(e =>
      e.type === 'credit_note' && e.syncStatus === 'failed'
    ).length
  }, [displayEntries])

  if (displayEntries.length === 0) {
    return (
      <div
        className={`text-center py-8 text-sm text-gray-500 ${className || ''}`}
      >
        <ReceiptIcon
          className="h-8 w-8 mx-auto mb-2 text-gray-300"
          aria-hidden="true"
        />
        <p>No payment or credit history</p>
      </div>
    )
  }

  return (
    <div className={cn('flow-root', className)}>
      {/* Failed sync attention banner */}
      {failedSyncCount > 0 && (
        <div className="mb-4 rounded-md bg-amber-50 border border-amber-200 p-3 text-sm">
          <div className="flex items-center gap-2 text-amber-800">
            <span className="font-medium">
              {failedSyncCount} credit note{failedSyncCount > 1 ? 's' : ''} failed to sync to Xero
            </span>
          </div>
          <p className="mt-1 text-xs text-amber-600">
            Click the retry button next to each credit note to attempt sync again.
          </p>
        </div>
      )}

      <ul className="-mb-6" role="list" aria-label="Payment and credit history">
        {displayEntries.map((entry, index) => (
          <li key={entry.id}>
            <TimelineItem
              entry={entry}
              isLast={index === displayEntries.length - 1}
              runningBalance={entry.runningBalance}
              onRetrySync={handleRetrySync}
              retryingId={retryingId}
            />
          </li>
        ))}
      </ul>
    </div>
  )
}

export default PaymentHistoryTimeline
